import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import {
  Avatar,
  Button,
  Card,
  Empty,
  Popover,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { localizeDigitsForLocale } from 'i18n/arabicDigits';
import { useCountryLabel } from 'hooks/useCountryLabel';
import {
  formatCalendarDayMonth,
  formatCalendarDayMonthYear,
  formatCalendarMonthYear,
  formatDisplayDate,
  formatDisplayDateRange,
  getCalendarWeekdayLabels,
} from 'i18n/calendarLabels';
import { DataLogStatus } from 'store/app/enums';
import { MappedReturn } from 'store/returns/models/returnData';
import { User } from 'store/auth/models';
import styled, { createGlobalStyle } from 'styled-components';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { getProviderLink } from 'utils/get-provider-link';
import { PreferenceActions } from 'utils/preferences';
import {
  getReturnActionLabel,
  isReturnActionPrimary,
} from 'utils/return-action-label';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { StatusGetter } from 'utils/status-getter';
import {
  CardField,
  DueDateField,
  EntityContainer,
  LastActivityField,
  ProviderLogo,
  SubmissionCard,
  UserItem,
  UserLabel,
  UsersContainer,
} from './BoardView';
import { ProviderIcon } from 'components/Custom/ProviderIcon';

const { Text } = Typography;

interface CalendarViewProps {
  mappedReturns: MappedReturn[];
  vatGenerate: (
    connectionId: string,
    mappingId: string,
    dataLogId?: string
  ) => void;
  loading?: boolean;
  visibleStatuses: string[];
  statusOrder?: string[];
  isClientLocked?: boolean;
  handlePreferences?: (action: any, preference: any) => any;
  currentUser?: User;
}

const CalendarWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 20px;
  background: var(--color-surface, #f3f7f8);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 8px;
  box-sizing: border-box;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 8px;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HideSubmittedToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MonthTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  min-width: 200px;
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  width: 100%;
`;

const DayHeader = styled.div`
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
  color: var(--color-text-muted, #5a6a6a);
  background: transparent;
`;

const DayCell = styled.div<{
  $isCurrentMonth?: boolean;
  $isToday?: boolean;
  $isWeekView?: boolean;
}>`
  min-height: ${(props) => (props.$isWeekView ? '400px' : '120px')};
  padding: 8px;
  background: ${(props) =>
    props.$isToday
      ? 'var(--color-calendar-today-bg, #e6eff2)'
      : 'var(--color-surface-raised, #FFFFFF)'};
  border: 1px solid
    ${(props) =>
      props.$isCurrentMonth
        ? 'var(--color-border, #dbeae3)'
        : 'var(--color-border, #f0f0f0)'};
  border-radius: 8px;
  position: relative;
  ${(props) => !props.$isCurrentMonth && 'opacity: 0.5;'}
`;

const DayNumber = styled.div<{ $isToday?: boolean }>`
  font-weight: ${(props) => (props.$isToday ? '600' : '400')};
  font-size: 14px;
  color: ${(props) =>
    props.$isToday
      ? 'var(--color-primary, #41aaaf)'
      : 'var(--color-text-base, var(--color-text-dark, #042a2a))'};
  margin-bottom: 8px;
`;

const ReturnCard = styled(Card)`
  margin-bottom: 6px;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid var(--color-border, #dbeae3) !important;
  background: var(--color-surface-base, #ffffff) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-card-body {
    padding: 8px;
    background: transparent !important;
  }
`;

const ReturnCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CalendarEntityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CalendarProviderLogo = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img,
  svg {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const EntityName = styled(Text)`
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const StatusBadge = styled.div`
  display: inline-block;
`;

const ReturnCount = styled.div`
  font-size: 10px;
  color: var(--color-text-muted, #5a6a6a);
  margin-top: 4px;
`;

const EmptyDay = styled.div`
  color: var(--color-calendar-empty-text, #d0d0d0);
  font-size: 11px;
  text-align: center;
  padding-top: 8px;
`;

// Day view styled components (similar to board cards)
const DayViewContainer = styled.div`
  width: 100%;
  padding: 20px;
  background: var(--color-surface, #f3f7f8);
  border-radius: 8px;
  box-sizing: border-box;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
`;

const DayViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const DayViewTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DayViewTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
`;

const DayViewCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;


const CalendarPopoverStyle = createGlobalStyle`
  .calendar-return-popover .ant-popover-inner {
    background-color: var(--color-surface-raised, #ffffff) !important;
    border-radius: 15px !important;
    padding: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border: 1px solid var(--color-border, transparent);
    color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  }
  
  .calendar-return-popover .ant-popover-inner-content {
    padding: 0 !important;
    color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  }
  
  .calendar-return-popover .ant-popover-arrow {
    display: none !important;
  }
`;

type CalendarViewType = 'month' | 'week' | 'day';

const HIDE_SUBMITTED_CALENDAR = 'hide-submitted-calendar';
const CALENDAR_VIEW_TYPE = 'calendar-view-type';

export const CalendarView: React.FC<CalendarViewProps> = ({
  mappedReturns,
  vatGenerate,
  loading = false,
  visibleStatuses,
  statusOrder,
  isClientLocked = false,
  handlePreferences,
  currentUser,
}) => {
  const { t, i18n } = useTranslation();
  const { localizeReturnLabel } = useCountryLabel();
  const formatMappingName = (mappingName?: string) =>
    mappingName
      ? localizeReturnLabel(mappingName.replace(/United Arab Emirates/gi, 'UAE'))
      : mappingName;
  const weekDays = useMemo(() => getCalendarWeekdayLabels(t), [t]);
  const [currentDate, setCurrentDate] = useState(moment());

  // Initialize viewType from saved preference or default to 'month'
  const [viewType, setViewType] = useState<CalendarViewType>(() => {
    if (handlePreferences) {
      const saved = handlePreferences(PreferenceActions.GET, {
        name: CALENDAR_VIEW_TYPE,
      });
      // Handle both string values and false (when preference not found)
      if (saved === 'month' || saved === 'week') {
        return saved;
      }
    }
    return 'month';
  });

  // Load preference on mount to ensure it's up to date
  useEffect(() => {
    if (handlePreferences) {
      const savedViewType = handlePreferences(PreferenceActions.GET, {
        name: CALENDAR_VIEW_TYPE,
      });
      // Only update if we have a valid saved preference
      if (savedViewType === 'month' || savedViewType === 'week') {
        setViewType(savedViewType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Get hide submitted preference (default to true if not set)
  const hideSubmittedSaved = handlePreferences
    ? handlePreferences(PreferenceActions.GET, {
        name: HIDE_SUBMITTED_CALENDAR,
      })
    : true;
  const [hideSubmitted, setHideSubmitted] = useState(
    hideSubmittedSaved !== undefined ? hideSubmittedSaved : true
  );

  // Day view state - when a day is clicked, show detailed view
  const [selectedDay, setSelectedDay] = useState<moment.Moment | null>(null);

  // Group returns by due date, filtering out submitted if hideSubmitted is true
  const returnsByDate = useMemo(() => {
    const grouped: Record<string, MappedReturn[]> = {};
    if (!mappedReturns || mappedReturns.length === 0) {
      return grouped;
    }
    mappedReturns.forEach((returnItem) => {
      if (!returnItem || !returnItem.nextDueDate) return;

      // Filter out submitted returns if hideSubmitted is true
      if (hideSubmitted && returnItem.status === DataLogStatus.SUBMITTED) {
        return;
      }

      try {
        // Use ISO string for moment to avoid deprecation when nextDueDate is a Date object
        const dateInput =
          returnItem.nextDueDate instanceof Date
            ? returnItem.nextDueDate.toISOString()
            : returnItem.nextDueDate;
        const dueDate = moment(dateInput).format('YYYY-MM-DD');
        if (!grouped[dueDate]) {
          grouped[dueDate] = [];
        }
        grouped[dueDate].push(returnItem);
      } catch (e) {
        // Skip invalid dates
        console.error(
          'Invalid date in return item:',
          returnItem.nextDueDate,
          e
        );
      }
    });
    return grouped;
  }, [mappedReturns, hideSubmitted]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    if (viewType === 'week') {
      // Generate days for the current week
      const weekStart = currentDate.clone().startOf('week');
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(weekStart.clone().add(i, 'days'));
      }
      return days;
    } else {
      // Generate days for the current month
      const monthStart = currentDate.clone().startOf('month');
      const monthEnd = currentDate.clone().endOf('month');
      const startDate = monthStart.clone().startOf('week');
      const endDate = monthEnd.clone().endOf('week');
      const days = [];
      const day = startDate.clone();
      while (day.isSameOrBefore(endDate, 'day')) {
        days.push(day.clone());
        day.add(1, 'day');
      }
      return days;
    }
  }, [currentDate, viewType]);

  const handlePrevious = () => {
    if (viewType === 'week') {
      setCurrentDate(currentDate.clone().subtract(1, 'week'));
    } else {
      setCurrentDate(currentDate.clone().subtract(1, 'month'));
    }
  };

  const handleNext = () => {
    if (viewType === 'week') {
      setCurrentDate(currentDate.clone().add(1, 'week'));
    } else {
      setCurrentDate(currentDate.clone().add(1, 'month'));
    }
  };

  const handleToday = () => {
    setCurrentDate(moment());
  };

  const handleReturnClick = (e: React.MouseEvent, record: MappedReturn) => {
    e.preventDefault();
    e.stopPropagation();
    vatGenerate(record.connectionId, record.mappingId, record.dataLog?.uuid);
  };

  const handleDayClick = (day: moment.Moment) => {
    setSelectedDay(day);
  };

  const handleBackToCalendar = () => {
    setSelectedDay(null);
  };

  const handlePreviousDay = () => {
    if (selectedDay) {
      setSelectedDay(selectedDay.clone().subtract(1, 'day'));
    }
  };

  const handleNextDay = () => {
    if (selectedDay) {
      setSelectedDay(selectedDay.clone().add(1, 'day'));
    }
  };

  const isDueDateExpired = (dueDate?: string): boolean => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.valueOf() < today.valueOf();
  };

  const getPeriod = (record: MappedReturn, status: string): string | null => {
    // Don't show period for Not Created status
    if (!status || status === DataLogStatus.NOT_STARTED) {
      return null;
    }
    if (record.dataLog?.fromReportingDate && record.dataLog?.toReportingDate) {
      return formatDisplayDateRange(
        record.dataLog.fromReportingDate,
        record.dataLog.toReportingDate,
        'DD/MM/YYYY',
        i18n.language
      );
    }
    return null;
  };

  const renderUsers = (record: MappedReturn, status: string) => {
    const users: Array<{ user: any; label: string }> = [];

    if (record.dataLog?.preparerUser) {
      users.push({
        user: record.dataLog.preparerUser,
        label: t('main.preparer'),
      });
    }

    if (record.dataLog?.reviewerUser) {
      users.push({
        user: record.dataLog.reviewerUser,
        label: t('main.reviewer'),
      });
    }

    if (record.dataLog?.clientUser && !isClientLocked) {
      users.push({
        user: record.dataLog.clientUser,
        label: t('main.client'),
      });
    }

    // Don't show N/A for Not Created status
    if (users.length === 0 && (!status || status !== '')) {
      return (
        <Text style={{ fontSize: '10px', color: 'var(--color-text-muted, #5a6a6a)' }}>
          {t('billing.not-available')}
        </Text>
      );
    }

    if (users.length === 0) {
      return null;
    }

    return (
      <UsersContainer>
        {users.map(({ user, label }, index) => (
          <UserItem key={index}>
            {renderAvatar(
              user.firstName || '',
              user.lastName || '',
              AvatarSizes.SMALL,
              user.userUuid,
              user.profilePicture
            )}
            <UserLabel>{label}</UserLabel>
          </UserItem>
        ))}
      </UsersContainer>
    );
  };

  // Render day view if a day is selected
  if (selectedDay) {
    const dayKey = selectedDay.format('YYYY-MM-DD');
    const dayReturns = returnsByDate[dayKey] || [];

    return (
      <>
        <CalendarPopoverStyle />
        <DayViewContainer>
          <DayViewHeader>
            <ScopeButton
              type="default"
              onClick={handleBackToCalendar}
              icon={<ArrowLeftOutlined />}
              width="auto"
              height="30px"
              fontSize="12px"
              lineheight="12px"
            >
              {t('submissions.back-to-calendar')}
            </ScopeButton>
            <DayViewTitleContainer>
              <Button
                icon={<LeftOutlined />}
                onClick={handlePreviousDay}
                type="text"
                aria-label={t('main.previous')}
                style={{ color: 'var(--color-text-dark, #042a2a)' }}
              />
              <DayViewTitle>
                {formatCalendarDayMonthYear(
                  selectedDay.date(),
                  selectedDay.month(),
                  selectedDay.year(),
                  t,
                  i18n.language
                )}
              </DayViewTitle>
              <Button
                icon={<RightOutlined />}
                onClick={handleNextDay}
                type="text"
                aria-label={t('main.next')}
                style={{ color: 'var(--color-text-dark, #042a2a)' }}
              />
            </DayViewTitleContainer>
            <div />
          </DayViewHeader>
          {dayReturns.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: '14px' }}>
                  {t('submissions.no-returns-for-this-date')}
                </span>
              }
              style={{
                padding: '60px 20px',
                background: 'var(--color-surface-raised, #FFFFFF)',
                borderRadius: '8px',
              }}
            />
          ) : (
            <DayViewCardsContainer>
              {dayReturns.map((record, index) => {
                if (!record) return null;
                const providerLink =
                  record.providerName && record.shortCode
                    ? getProviderLink(
                        record.providerName.toLowerCase(),
                        record.shortCode
                      )
                    : null;
                const status = record.status || '';
                const period = getPeriod(record, status);
                const isExpired = isDueDateExpired(record.nextDueDate);
                const uniqueKey = record.dataLog?.uuid
                  ? `${record.mappingId}-${record.dataLog.uuid}`
                  : `${record.connectionId}-${record.mappingId}`;

                return (
                  <SubmissionCard
                    key={`${uniqueKey}-${index}`}
                    onClick={(e) => handleReturnClick(e, record)}
                  >
                    <EntityContainer>
                      {record.providerName && providerLink && (
                        <ProviderLogo
                          href={providerLink}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ProviderIcon
                            providerName={record.providerName}
                            alt={record.providerName}
                            style={{ height: '24px', width: 'auto' }}
                          />
                        </ProviderLogo>
                      )}
                      <Text
                        ellipsis={{
                          tooltip: (record.organisation || 'N/A').replace(
                            /United Arab Emirates/gi,
                            'UAE'
                          ),
                        }}
                        style={{
                          fontSize: 13,
                          color: 'var(--color-text-dark, #042a2a)',
                          fontWeight: 500,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Link
                          to={`/entities/organisation/${record.organisationId}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearOrganisationDetailsTabStorage();
                          }}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#41aaaf';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--color-text-dark, #042a2a)';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          {(record.organisation || 'N/A').replace(
                            /United Arab Emirates/gi,
                            'UAE'
                          )}
                        </Link>
                      </Text>
                    </EntityContainer>

                    <Text
                      ellipsis={{
                        tooltip: record.mappingName
                          ? formatMappingName(record.mappingName)
                          : undefined,
                      }}
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'var(--color-text-dark, #042a2a)',
                        display: 'block',
                        marginBottom: 12,
                      }}
                    >
                      {formatMappingName(record.mappingName)}
                    </Text>

                    {period && (
                      <CardField>
                        {t('submissions.period-label')} {period}
                      </CardField>
                    )}

                    {record.nextDueDate && (
                      <DueDateField $isExpired={isExpired}>
                        {t('submissions.due-label')}{' '}
                        {formatDisplayDate(
                          record.nextDueDate,
                          'DD/MM/YYYY',
                          i18n.language
                        )}
                      </DueDateField>
                    )}

                    {renderUsers(record, status)}

                    {record.dataLog?.lastUpdatedDate && (
                      <LastActivityField>
                        {t('submissions.last-activity-label')}{' '}
                        {formatDisplayDate(
                          record.dataLog.lastUpdatedDate,
                          'DD/MM/YYYY',
                          i18n.language
                        )}
                      </LastActivityField>
                    )}
                  </SubmissionCard>
                );
              })}
            </DayViewCardsContainer>
          )}
        </DayViewContainer>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <CalendarPopoverStyle />
        <CalendarWrapper>
          <CalendarHeader>
            <MonthNavigation>
              <Button
                icon={<LeftOutlined />}
                disabled
                aria-label={t('main.previous')}
              />
              <MonthTitle>{t('submissions.loading')}</MonthTitle>
              <Button
                icon={<RightOutlined />}
                disabled
                aria-label={t('main.next')}
              />
            </MonthNavigation>
            <ViewToggle>
              <HideSubmittedToggle>
                <span style={{ fontSize: 12 }}>
                  {t('submissions.hide-submitted')}
                </span>
                <Tooltip
                  title={
                    hideSubmitted
                      ? t('common.toggle-on-click-to-turn-off')
                      : t('common.toggle-off-click-to-turn-on')
                  }
                >
                  <Switch
                    size={'small'}
                    checked={hideSubmitted}
                    className={hideSubmitted ? 'switch-checked' : ''}
                    aria-label={t('submissions.hide-submitted')}
                    onChange={(checked) => {
                      setHideSubmitted(checked);
                      if (handlePreferences) {
                        handlePreferences(PreferenceActions.SET, {
                          name: HIDE_SUBMITTED_CALENDAR,
                          value: checked,
                        });
                      }
                    }}
                  />
                </Tooltip>
              </HideSubmittedToggle>
              <ScopeButton
                type={viewType === 'month' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => {
                  setViewType('month');
                  if (handlePreferences) {
                    handlePreferences(PreferenceActions.SET, {
                      name: CALENDAR_VIEW_TYPE,
                      value: 'month',
                    });
                  }
                }}
                width="auto"
                height="30px"
                fontSize="12px"
                lineheight="12px"
              >
                {t('submissions.month')}
              </ScopeButton>
              <ScopeButton
                type={viewType === 'week' ? 'primary' : 'default'}
                icon={<UnorderedListOutlined />}
                onClick={() => {
                  setViewType('week');
                  if (handlePreferences) {
                    handlePreferences(PreferenceActions.SET, {
                      name: CALENDAR_VIEW_TYPE,
                      value: 'week',
                    });
                  }
                }}
                width="auto"
                height="30px"
                fontSize="12px"
                lineheight="12px"
              >
                {t('submissions.week')}
              </ScopeButton>
              <ScopeButton
                icon={<CalendarOutlined />}
                onClick={handleToday}
                width="auto"
                height="30px"
                fontSize="12px"
                lineheight="12px"
              >
                {t('submissions.today')}
              </ScopeButton>
            </ViewToggle>
          </CalendarHeader>
          <CalendarGrid>
            {Array.from({ length: viewType === 'week' ? 7 : 35 }).map(
              (_, i) => (
                <DayCell
                  key={i}
                  $isCurrentMonth
                  $isWeekView={viewType === 'week'}
                >
                  <Skeleton active paragraph={{ rows: 2 }} />
                </DayCell>
              )
            )}
          </CalendarGrid>
        </CalendarWrapper>
      </>
    );
  }

  return (
    <>
      <CalendarPopoverStyle />
      <CalendarWrapper>
        <CalendarHeader>
          <MonthNavigation>
            <Button
              icon={<LeftOutlined />}
              onClick={handlePrevious}
              type="text"
              aria-label={t('main.previous')}
              style={{ color: 'var(--color-text-dark, #042a2a)' }}
            />
            <MonthTitle>
              {viewType === 'week'
                ? (() => {
                    const weekStart = currentDate.clone().startOf('week');
                    const weekEnd = currentDate.clone().endOf('week');
                    return localizeDigitsForLocale(
                      `${formatCalendarDayMonth(
                        weekStart.date(),
                        weekStart.month(),
                        t,
                        i18n.language
                      )} - ${formatCalendarDayMonth(
                        weekEnd.date(),
                        weekEnd.month(),
                        t,
                        i18n.language
                      )} ${weekEnd.year()}`,
                      i18n.language
                    );
                  })()
                : formatCalendarMonthYear(
                    currentDate.month(),
                    currentDate.year(),
                    t,
                    i18n.language
                  )}
            </MonthTitle>
            <Button
              icon={<RightOutlined />}
              onClick={handleNext}
              type="text"
              aria-label={t('main.next')}
              style={{ color: 'var(--color-text-dark, #042a2a)' }}
            />
          </MonthNavigation>
          <ViewToggle>
            <HideSubmittedToggle>
              <span style={{ fontSize: 12 }}>
                {t('submissions.hide-submitted')}
              </span>
              <Tooltip
                title={
                  hideSubmitted
                    ? t('common.toggle-on-click-to-turn-off')
                    : t('common.toggle-off-click-to-turn-on')
                }
              >
                <Switch
                  size={'small'}
                  checked={hideSubmitted}
                  className={hideSubmitted ? 'switch-checked' : ''}
                  aria-label={t('submissions.hide-submitted')}
                  onChange={(checked) => {
                    setHideSubmitted(checked);
                    if (handlePreferences) {
                      handlePreferences(PreferenceActions.SET, {
                        name: HIDE_SUBMITTED_CALENDAR,
                        value: checked,
                      });
                    }
                  }}
                />
              </Tooltip>
            </HideSubmittedToggle>
            <ScopeButton
              type={viewType === 'month' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => {
                setViewType('month');
                if (handlePreferences) {
                  handlePreferences(PreferenceActions.SET, {
                    name: CALENDAR_VIEW_TYPE,
                    value: 'month',
                  });
                }
              }}
              width="auto"
              height="30px"
              fontSize="12px"
              lineheight="12px"
            >
              {t('submissions.month')}
            </ScopeButton>
            <ScopeButton
              type={viewType === 'week' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => {
                setViewType('week');
                if (handlePreferences) {
                  handlePreferences(PreferenceActions.SET, {
                    name: CALENDAR_VIEW_TYPE,
                    value: 'week',
                  });
                }
              }}
              width="auto"
              height="30px"
              fontSize="12px"
              lineheight="12px"
            >
              {t('submissions.week')}
            </ScopeButton>
            <ScopeButton
              icon={<CalendarOutlined />}
              onClick={handleToday}
              type="default"
              width="auto"
              height="30px"
              fontSize="12px"
              lineheight="12px"
            >
              {t('submissions.today')}
            </ScopeButton>
          </ViewToggle>
        </CalendarHeader>

        <CalendarGrid>
          {weekDays.map((day) => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
          {calendarDays.map((day, index) => {
            const dayKey = day.format('YYYY-MM-DD');
            const dayReturns = returnsByDate[dayKey] || [];
            const isCurrentMonth = day.month() === currentDate.month();
            const isToday = day.isSame(moment(), 'day');

            return (
              <Tooltip
                key={`${dayKey}-${index}`}
                title={t('submissions.click-to-open-day-view')}
                placement="top"
              >
                <DayCell
                  $isCurrentMonth={isCurrentMonth}
                  $isToday={isToday}
                  $isWeekView={viewType === 'week'}
                  onClick={() => handleDayClick(day)}
                  style={{ cursor: 'pointer' }}
                >
                  <DayNumber $isToday={isToday}>
                    {formatCalendarDayMonth(
                      day.date(),
                      day.month(),
                      t,
                      i18n.language
                    )}
                  </DayNumber>
                  {dayReturns.length > 0 ? (
                    <>
                      {dayReturns
                        .slice(0, viewType === 'week' ? 10 : 3)
                        .map((record, recordIndex) => {
                          if (!record) return null;
                          const providerLink =
                            record.providerName && record.shortCode
                              ? getProviderLink(
                                  record.providerName.toLowerCase(),
                                  record.shortCode
                                )
                              : null;
                          const status = record.status || '';
                          const period = getPeriod(record, status);

                          const detailedContent = (
                            <div
                              style={{
                                minWidth: '250px',
                                padding: '8px',
                                fontFamily: 'Inter, sans-serif',
                              }}
                            >
                              {/* Provider and Return Name */}
                              <div
                                style={{
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                {record.providerName && (
                                  <ProviderIcon
                                    providerName={record.providerName}
                                    alt={record.providerName}
                                    style={{ height: '20px', width: 'auto' }}
                                  />
                                )}
                                <div style={{ flex: 1 }}>
                                  {record.mappingName && (
                                    <div
                                      style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: 'var(--color-text-dark, #042a2a)',
                                        marginBottom: '2px',
                                      }}
                                    >
                                      {formatMappingName(record.mappingName)}
                                    </div>
                                  )}
                                  {record.organisation && (
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: 'var(--color-text-muted, #5a6a6a)',
                                      }}
                                    >
                                      {record.organisation}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Status */}
                              <div style={{ marginBottom: '12px' }}>
                                <StatusBadge>
                                  <StatusGetter
                                    status={status}
                                    noDraft={false}
                                  />
                                </StatusBadge>
                              </div>

                              {/* Period */}
                              {period && (
                                <div
                                  style={{
                                    marginBottom: '12px',
                                    fontSize: '12px',
                                    color: 'var(--color-text-muted, #5a6a6a)',
                                  }}
                                >
                                  {t('submissions.period-label')} {period}
                                </div>
                              )}

                              {/* Users with Labels */}
                              <div style={{ marginBottom: '12px' }}>
                                {record.dataLog?.preparerUser && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '6px',
                                    }}
                                  >
                                    <Avatar
                                      style={{
                                        backgroundColor: 'var(--color-text-muted, #5a6a6a)',
                                        verticalAlign: 'middle',
                                        fontSize: 10,
                                      }}
                                      size={24}
                                      src={
                                        record.dataLog.preparerUser
                                          .profilePicture
                                          ? record.dataLog.preparerUser.profilePicture.startsWith(
                                              'data:'
                                            )
                                            ? record.dataLog.preparerUser
                                                .profilePicture
                                            : `data:image/png;base64,${record.dataLog.preparerUser.profilePicture}`
                                          : undefined
                                      }
                                    >
                                      {!record.dataLog.preparerUser
                                        .profilePicture &&
                                        `${
                                          record.dataLog.preparerUser.firstName?.charAt(
                                            0
                                          ) || ''
                                        }${
                                          record.dataLog.preparerUser.lastName?.charAt(
                                            0
                                          ) || ''
                                        }`}
                                    </Avatar>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        color: 'var(--color-text-dark, #042a2a)',
                                      }}
                                    >
                                      <strong>{t('submissions.preparer-label')}</strong>{' '}
                                      {`${
                                        record.dataLog.preparerUser.firstName ||
                                        ''
                                      } ${
                                        record.dataLog.preparerUser.lastName ||
                                        ''
                                      }`.trim() ||
                                        record.dataLog.preparerUser.email ||
                                        t('common.unknown')}
                                    </span>
                                  </div>
                                )}
                                {record.dataLog?.reviewerUser && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '6px',
                                    }}
                                  >
                                    <Avatar
                                      style={{
                                        backgroundColor: 'var(--color-text-muted, #5a6a6a)',
                                        verticalAlign: 'middle',
                                        fontSize: 10,
                                      }}
                                      size={24}
                                      src={
                                        record.dataLog.reviewerUser
                                          .profilePicture
                                          ? record.dataLog.reviewerUser.profilePicture.startsWith(
                                              'data:'
                                            )
                                            ? record.dataLog.reviewerUser
                                                .profilePicture
                                            : `data:image/png;base64,${record.dataLog.reviewerUser.profilePicture}`
                                          : undefined
                                      }
                                    >
                                      {!record.dataLog.reviewerUser
                                        .profilePicture &&
                                        `${
                                          record.dataLog.reviewerUser.firstName?.charAt(
                                            0
                                          ) || ''
                                        }${
                                          record.dataLog.reviewerUser.lastName?.charAt(
                                            0
                                          ) || ''
                                        }`}
                                    </Avatar>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        color: 'var(--color-text-dark, #042a2a)',
                                      }}
                                    >
                                      <strong>{t('submissions.reviewer-label')}</strong>{' '}
                                      {`${
                                        record.dataLog.reviewerUser.firstName ||
                                        ''
                                      } ${
                                        record.dataLog.reviewerUser.lastName ||
                                        ''
                                      }`.trim() ||
                                        record.dataLog.reviewerUser.email ||
                                        t('common.unknown')}
                                    </span>
                                  </div>
                                )}
                                {record.dataLog?.clientUser &&
                                  !isClientLocked && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '6px',
                                      }}
                                    >
                                      <Avatar
                                        style={{
                                          backgroundColor: 'var(--color-text-muted, #5a6a6a)',
                                          verticalAlign: 'middle',
                                          fontSize: 10,
                                        }}
                                        size={24}
                                        src={
                                          record.dataLog.clientUser
                                            .profilePicture
                                            ? record.dataLog.clientUser.profilePicture.startsWith(
                                                'data:'
                                              )
                                              ? record.dataLog.clientUser
                                                  .profilePicture
                                              : `data:image/png;base64,${record.dataLog.clientUser.profilePicture}`
                                            : undefined
                                        }
                                      >
                                        {!record.dataLog.clientUser
                                          .profilePicture &&
                                          `${
                                            record.dataLog.clientUser.firstName?.charAt(
                                              0
                                            ) || ''
                                          }${
                                            record.dataLog.clientUser.lastName?.charAt(
                                              0
                                            ) || ''
                                          }`}
                                      </Avatar>
                                      <span
                                        style={{
                                          fontSize: '12px',
                                          color: 'var(--color-text-dark, #042a2a)',
                                        }}
                                      >
                                        <strong>{t('submissions.client-label')}</strong>{' '}
                                        {`${
                                          record.dataLog.clientUser.firstName ||
                                          ''
                                        } ${
                                          record.dataLog.clientUser.lastName ||
                                          ''
                                        }`.trim() ||
                                          record.dataLog.clientUser.email ||
                                          t('common.unknown')}
                                      </span>
                                    </div>
                                  )}
                              </div>

                              {/* Create/Open Button */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  marginTop: '8px',
                                }}
                              >
                                <ScopeButton
                                  type={
                                    isReturnActionPrimary(record, currentUser)
                                      ? 'primary'
                                      : 'default'
                                  }
                                  width="70px"
                                  height="25px"
                                  fontSize="12px"
                                  lineheight="12px"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReturnClick(e, record);
                                  }}
                                >
                                  {getReturnActionLabel(record, t, currentUser)}
                                </ScopeButton>
                              </div>
                            </div>
                          );

                          return (
                            <Popover
                              key={`${record.mappingId}-${
                                record.dataLog?.uuid || record.connectionId
                              }-${recordIndex}`}
                              content={detailedContent}
                              trigger="hover"
                              placement="right"
                              overlayStyle={{
                                maxWidth: '350px',
                                zIndex: 9999,
                              }}
                              classNames={{ root: 'calendar-return-popover' }}
                              destroyOnHidden={true}
                            >
                              <ReturnCard
                                onClick={(e) => handleReturnClick(e, record)}
                                size="small"
                              >
                                <ReturnCardContent>
                                  <CalendarEntityContainer>
                                    {record.providerName && providerLink && (
                                      <CalendarProviderLogo
                                        href={providerLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ProviderIcon
                                          providerName={record.providerName}
                                          alt={record.providerName}
                                          width={16}
                                          height={16}
                                        />
                                      </CalendarProviderLogo>
                                    )}
                                    <EntityName
                                      ellipsis={{
                                        tooltip: record.organisation || 'N/A',
                                      }}
                                    >
                                      {record.organisation || 'N/A'}
                                    </EntityName>
                                  </CalendarEntityContainer>
                                </ReturnCardContent>
                              </ReturnCard>
                            </Popover>
                          );
                        })}
                      {dayReturns.length > (viewType === 'week' ? 10 : 3) && (
                        <ReturnCount>
                          +{dayReturns.length - (viewType === 'week' ? 10 : 3)}{' '}
                          more
                        </ReturnCount>
                      )}
                    </>
                  ) : (
                    <EmptyDay>{t('submissions.no-returns')}</EmptyDay>
                  )}
                </DayCell>
              </Tooltip>
            );
          })}
        </CalendarGrid>
      </CalendarWrapper>
    </>
  );
};
